import { useMemo, useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  PurchaseOrder,
  PurchaseOrderLine,
} from "@/redux/services/inventory/inventory.type";
import { useReceivePurchaseOrderMutation } from "@/redux/services/inventory/purchaseOrder.api";
import {
  formatStock,
  normalizeDecimalInput,
  stripTrailingZeros,
} from "@/lib/utils";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/apiErrors";

interface ReceiveGoodsModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
}

const isPackagingLine = (line: PurchaseOrderLine) =>
  !!line.packaging && line.packageQuantityOrdered !== null;

const remainingFor = (line: PurchaseOrderLine) => {
  if (isPackagingLine(line)) {
    const ordered = parseFloat(line.packageQuantityOrdered ?? "0") || 0;
    const received = parseFloat(line.packageQuantityReceived ?? "0") || 0;
    return Math.max(0, ordered - received).toFixed(2);
  }
  const ordered = parseFloat(line.quantityOrdered) || 0;
  const received = parseFloat(line.quantityReceived) || 0;
  return Math.max(0, ordered - received).toFixed(2);
};

/** "1,5" (French keypad) -> 1.5; empty or malformed -> 0. */
const parseQty = (value: string | undefined) =>
  parseFloat(normalizeDecimalInput(value ?? "") ?? "") || 0;

const formatLineQty = (line: PurchaseOrderLine, value: string) => {
  if (isPackagingLine(line)) {
    return `${stripTrailingZeros(value)} ${line.packaging?.name ?? ""}`;
  }
  return formatStock(value, line.ingredient.baseUnit);
};

const ReceiveGoodsModal = ({
  isOpen,
  onClose,
  purchaseOrder,
}: ReceiveGoodsModalProps) => {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      purchaseOrder.lines.map((line) => [line.id, remainingFor(line)])
    )
  );

  const [receive, { isLoading }] = useReceivePurchaseOrderMutation();

  const overreceiving = useMemo(() => {
    return purchaseOrder.lines.some((line) => {
      const qty = parseQty(values[line.id]);
      const remain = parseFloat(remainingFor(line));
      return qty > remain;
    });
  }, [purchaseOrder.lines, values]);

  const invalidInput = purchaseOrder.lines.some((line) => {
    const typed = values[line.id] ?? "";
    return typed.trim() !== "" && normalizeDecimalInput(typed) === null;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (invalidInput) {
      toast.error("Quantité invalide : saisissez un nombre (ex. 1,5)");
      return;
    }
    const receivedLines = purchaseOrder.lines
      .map((line) => {
        const raw = normalizeDecimalInput(values[line.id] ?? "") ?? "0";
        if (parseFloat(raw) <= 0) return null;
        if (isPackagingLine(line)) {
          return { lineUuid: line.id, packageQuantityReceived: raw };
        }
        return { lineUuid: line.id, receivedQuantity: raw };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null);

    if (receivedLines.length === 0) {
      toast.error("Saisissez au moins une quantité reçue");
      return;
    }

    const res = await receive({
      uuid: purchaseOrder.id,
      data: { receivedLines },
    });

    if ("data" in res) {
      toast.success("Réception enregistrée");
      onClose();
    } else {
      toastApiError(res.error, "Erreur lors de la réception");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Réception ${purchaseOrder.poNumber}`}
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Ingrédient
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Commandé
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Déjà reçu
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                  Restant
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground w-36">
                  À recevoir maintenant
                </th>
              </tr>
            </thead>
            <tbody>
              {purchaseOrder.lines.map((line) => {
                const remain = remainingFor(line);
                const inputValue = values[line.id] ?? "";
                const qty = parseQty(inputValue);
                const over = qty > parseFloat(remain);
                const usePackaging = isPackagingLine(line);
                const orderedDisplay = usePackaging
                  ? formatLineQty(line, line.packageQuantityOrdered ?? "0")
                  : formatStock(line.quantityOrdered, line.ingredient.baseUnit);
                const receivedDisplay = usePackaging
                  ? formatLineQty(line, line.packageQuantityReceived ?? "0")
                  : formatStock(line.quantityReceived, line.ingredient.baseUnit);
                const remainDisplay = formatLineQty(line, remain);
                const baseEquivalent =
                  usePackaging && qty > 0 && line.packaging
                    ? qty * (parseFloat(line.packaging.quantityInBase) || 0)
                    : 0;
                return (
                  <tr key={line.id} className="border-b border-border">
                    <td className="py-2 px-3 text-sm font-medium">
                      {line.ingredient.name}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">
                      {orderedDisplay}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">
                      {receivedDisplay}
                    </td>
                    <td className="py-2 px-3 text-sm text-card-foreground">
                      {remainDisplay}
                    </td>
                    <td className="py-2 px-3">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={inputValue}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [line.id]: e.target.value,
                          }))
                        }
                        className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                          over ? "border-danger" : "border-border"
                        }`}
                        placeholder="0.00"
                      />
                      {usePackaging && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {line.packaging?.name}
                          {baseEquivalent > 0 && (
                            <>
                              {" "}
                              · = {baseEquivalent} {line.ingredient.baseUnit}
                            </>
                          )}
                        </p>
                      )}
                      {over && (
                        <p className="text-xs text-danger mt-1">
                          Dépasse le restant
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          Toute quantité excédentaire sera automatiquement plafonnée par le
          serveur.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={overreceiving}
          >
            Confirmer la réception
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ReceiveGoodsModal;
